/**
 * 统计分析工具模块
 * 提供更精确的统计计算方法
 */

import type { MADResult, PercentileData, HistogramData, HistogramBin } from '../types';
import { CONFIG } from '../config';

/**
 * 计算数组的和
 */
export function sum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

/**
 * 计算平均值
 */
export function mean(arr: number[]): number {
  if (arr.length === 0) return 0;
  return sum(arr) / arr.length;
}

/**
 * 计算中位数
 */
export function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * 计算标准差
 */
export function standardDeviation(arr: number[], avg?: number): number {
  if (arr.length < 2) return 0;
  const m = avg ?? mean(arr);
  const squaredDiffs = arr.map(x => Math.pow(x - m, 2));
  return Math.sqrt(sum(squaredDiffs) / (arr.length - 1));
}

/**
 * 计算百分位数
 * 使用线性插值方法提高精度
 */
export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0];

  const index = p * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (upper >= sorted.length) return sorted[sorted.length - 1];
  if (lower === upper) return sorted[lower];

  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
}

/**
 * 计算多个百分位数
 */
export function computePercentiles(intervals: number[]): PercentileData {
  const sorted = [...intervals].sort((a, b) => a - b);

  return {
    p50: percentile(sorted, 0.50),
    p90: percentile(sorted, 0.90),
    p95: percentile(sorted, 0.95),
    p99: percentile(sorted, 0.99),
    p999: percentile(sorted, 0.999),
    p9999: percentile(sorted, 0.9999),
  };
}

/**
 * 计算中位数绝对偏差 (MAD)
 * MAD 是一种稳健的离散度量，对异常值不敏感
 */
export function computeMAD(values: number[]): MADResult {
  if (values.length === 0) {
    return { median: 0, mad: 0, scaledMAD: 0 };
  }

  const med = median(values);
  const deviations = values.map(x => Math.abs(x - med));
  const madValue = median(deviations);

  // 缩放后的 MAD 可以作为标准差的稳健估计
  const scaledMAD = madValue * CONFIG.analysis.madScaleFactor;

  return {
    median: med,
    mad: madValue,
    scaledMAD,
  };
}

/**
 * 使用 Winsorized 方法计算稳健均值
 * 将极端值限制在指定百分位
 */
export function winsorizedMean(arr: number[], percentileLow = 0.05, percentileHigh = 0.95): number {
  if (arr.length === 0) return 0;

  const sorted = [...arr].sort((a, b) => a - b);
  const low = percentile(sorted, percentileLow);
  const high = percentile(sorted, percentileHigh);

  const winsorized = arr.map(x => {
    if (x < low) return low;
    if (x > high) return high;
    return x;
  });

  return mean(winsorized);
}

/**
 * 计算四分位距 (IQR)
 */
export function computeIQR(sorted: number[]): { q1: number; q3: number; iqr: number } {
  const q1 = percentile(sorted, 0.25);
  const q3 = percentile(sorted, 0.75);
  return { q1, q3, iqr: q3 - q1 };
}

/**
 * 检测异常值 (使用 IQR 方法)
 */
export function detectOutliersIQR(values: number[], k = 1.5): {
  inliers: number[];
  outliers: number[];
  bounds: { lower: number; upper: number };
} {
  const sorted = [...values].sort((a, b) => a - b);
  const { q1, q3, iqr } = computeIQR(sorted);

  const lower = q1 - k * iqr;
  const upper = q3 + k * iqr;

  const inliers: number[] = [];
  const outliers: number[] = [];

  for (const v of values) {
    if (v < lower || v > upper) {
      outliers.push(v);
    } else {
      inliers.push(v);
    }
  }

  return { inliers, outliers, bounds: { lower, upper } };
}

/**
 * 检测异常值 (使用 MAD 方法)
 */
export function detectOutliersMAD(values: number[], threshold = 3): {
  inliers: number[];
  outliers: number[];
  bounds: { lower: number; upper: number };
} {
  const { median: med, scaledMAD } = computeMAD(values);

  const lower = med - threshold * scaledMAD;
  const upper = med + threshold * scaledMAD;

  const inliers: number[] = [];
  const outliers: number[] = [];

  for (const v of values) {
    if (v < lower || v > upper) {
      outliers.push(v);
    } else {
      inliers.push(v);
    }
  }

  return { inliers, outliers, bounds: { lower, upper } };
}

/**
 * 计算变异系数 (CV)
 */
export function coefficientOfVariation(arr: number[]): number {
  const m = mean(arr);
  if (m === 0) return 0;
  return (standardDeviation(arr, m) / m) * 100;
}

/**
 * 生成直方图数据
 */
export function generateHistogram(
  values: number[],
  numBins?: number,
  focusRange?: { min: number; max: number }
): HistogramData {
  if (values.length === 0) {
    return {
      bins: [],
      min: 0,
      max: 0,
      binSize: 0,
      outlierCount: 0,
      outlierThreshold: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const dataMin = sorted[0];
  const dataMax = sorted[sorted.length - 1];

  // 确定显示范围
  let displayMin: number;
  let displayMax: number;
  let outlierThreshold: number;

  if (focusRange) {
    displayMin = focusRange.min;
    displayMax = focusRange.max;
    outlierThreshold = focusRange.max;
  } else {
    // 自动聚焦：如果范围太大，聚焦到 P95 附近
    const range = dataMax - dataMin;
    const med = median(sorted);

    if (range > med * 10) {
      const p95 = percentile(sorted, 0.95);
      displayMin = dataMin;
      displayMax = Math.min(dataMax, p95 * 1.5);
      outlierThreshold = displayMax;
    } else {
      displayMin = dataMin;
      displayMax = dataMax;
      outlierThreshold = dataMax;
    }
  }

  const bins = numBins ?? CONFIG.analysis.histogramBins;
  const binSize = (displayMax - displayMin) / bins;

  const counts = new Array(bins).fill(0);
  let outlierCount = 0;

  for (const val of values) {
    if (val > outlierThreshold) {
      outlierCount++;
    } else {
      const binIndex = Math.min(
        Math.floor((val - displayMin) / binSize),
        bins - 1
      );
      if (binIndex >= 0) {
        counts[binIndex]++;
      }
    }
  }

  const totalInRange = values.length - outlierCount;

  const histogramBins: HistogramBin[] = counts.map((count, i) => {
    const start = displayMin + i * binSize;
    const end = start + binSize;
    const binCenter = (start + end) / 2;

    // 检测是否是量化值（靠近100μs的整数倍）
    const isQuantized = Math.abs(binCenter % 100) < binSize / 2;

    return {
      start,
      end,
      count,
      percentage: totalInRange > 0 ? (count / totalInRange) * 100 : 0,
      isQuantized,
    };
  });

  return {
    bins: histogramBins,
    min: displayMin,
    max: displayMax,
    binSize,
    outlierCount,
    outlierThreshold,
  };
}

/**
 * 计算 IQR 截断平均值
 * 去除 IQR 范围外的异常值后计算平均值
 * 这比简单平均更稳健，同时保留了平均值的统计效率
 */
export function trimmedMeanIQR(arr: number[], k = 1.5): number {
  if (arr.length === 0) return 0;
  if (arr.length < 4) return median(arr); // 样本太少时使用中位数

  const { inliers } = detectOutliersIQR(arr, k);

  // 如果过滤后样本太少，回退到中位数
  if (inliers.length < arr.length * 0.5) {
    return median(arr);
  }

  return mean(inliers);
}

/**
 * 批处理间隔阈值（微秒）
 * 小于此值的间隔被认为是浏览器事件批处理导致的，不是真实的设备轮询间隔
 * 即使 8000Hz 设备，真实间隔也是 125μs，不可能低于 10μs
 */
const BATCH_THRESHOLD_US = 10;

/**
 * 估计真实轮询率（使用稳健统计方法）
 *
 * 处理步骤：
 * 1. 先排除批处理导致的极小间隔（< 10μs）- 这些不是真实轮询间隔
 * 2. 使用 IQR 方法排除异常值（系统卡顿导致的长间隔）
 * 3. 对正常范围内的数据计算平均值
 *
 * 这种方法能有效抵抗：
 * - 浏览器事件批处理导致的 0μs 间隔
 * - 系统调度延迟导致的偶发长间隔
 * - GC 暂停等造成的数据抖动
 */
export function estimateTruePollingRate(intervals: number[]): number {
  if (intervals.length === 0) return 0;

  // 第一步：排除批处理导致的极小间隔
  // 浏览器精度约 100μs，当多个事件在同一时刻到达时会产生 0μs 或极小间隔
  const validIntervals = intervals.filter(i => i >= BATCH_THRESHOLD_US);

  // 如果过滤后数据太少，说明设备轮询率超过浏览器测量能力
  // 此时使用总时间/总样本数的方式估算
  if (validIntervals.length < intervals.length * 0.3) {
    // 大部分数据被过滤，使用备用方法：
    // 计算总时间跨度除以样本数（相当于假设每个事件都是一次轮询）
    const totalTime = sum(intervals);
    const avgInterval = totalTime / intervals.length;
    return avgInterval > 0 ? 1000000 / avgInterval : 0;
  }

  // 第二步：使用 IQR 截断平均计算稳健的间隔估计
  const robustInterval = trimmedMeanIQR(validIntervals);

  // 防止除以零
  if (robustInterval <= 0) return 0;

  return 1000000 / robustInterval; // 转换为 Hz
}
